import { Cancel } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { Button, CircularProgress } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { User } from '@src/types/User'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export function useManageUsersColumns(manageAccount: (user: User) => void, updatingUserEmail: string | null) {
  const { t } = useTranslation()

  const columns = useMemo<GridColDef<User>[]>(
    () => [
      {
        field: 'userFullName',
        headerName: t('UserStatsHeaders.userFullName'),
        description: t('UserStatsHeaders.userFullName'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'accountEnabled',
        headerName: t('UserStatsHeaders.accountStatus'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              height: '100%',
            }}
          >
            {params.value ? <CheckIcon color="success" /> : <Cancel color="error" />}
          </div>
        ),
      },
      {
        field: 'merchant',
        headerName: t('UserStatsHeaders.merchant'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'language',
        headerName: t('UserStatsHeaders.language'),
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'jobTitle',
        headerName: t('UserStatsHeaders.jobTitle'),
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'lastConnection',
        headerName: t('UserStatsHeaders.lastConnection'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'allApplications',
        headerName: t('UserStatsHeaders.allApplications'),
        align: 'center',
        headerAlign: 'center',
        flex: 1.2,
      },
      { field: 'active', headerName: t('UserStatsHeaders.active'), flex: 0.5, align: 'center', headerAlign: 'center' },
      {
        field: 'completed',
        headerName: t('UserStatsHeaders.completed'),
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
      },
      { field: 'draft', headerName: t('UserStatsHeaders.draft'), flex: 0.5, align: 'center', headerAlign: 'center' },
      {
        field: 'expired',
        headerName: t('UserStatsHeaders.expired'),
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'cancelled',
        headerName: t('UserStatsHeaders.cancelled'),
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
      },

      {
        field: 'toggleAccount',
        headerName: t('UserStatsHeaders.manageAccount'),
        flex: 1,
        renderCell: (params: { row: User }) =>
          updatingUserEmail === params.row.email ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                height: '100%',
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => manageAccount(params.row)}
              disabled={updatingUserEmail !== null && updatingUserEmail !== params.row.email}
            >
              {params.row.accountEnabled ? `${t('common.deactivate')}` : `${t('common.activate')}`}
            </Button>
          ),
      },
    ],
    [t, manageAccount, updatingUserEmail],
  )
  return columns
}
